import type { AddressValidationResult as ZeusAddressValidationResult } from '../__generated__/zeus/types';
import {
  sellerAddressRequiresUnitSelection,
  standardizeSellerAddress,
} from '../components/api/sellerAddressIntake';
import { ADDRESS_INVALID_RESULT } from './AddressValidationContext';
import type { gateProceedToOnboardingFlow, SelectedAddress } from './AddressValidationContext';

export type SellerAddressLookup = {
  standardizeAddress: typeof standardizeSellerAddress;
  requiresUnitSelection: typeof sellerAddressRequiresUnitSelection;
};

export type SellerAddressFlow = SellerAddressLookup & {
  proceed: typeof gateProceedToOnboardingFlow;
};

const STREET_TOKEN_ALIASES: Readonly<Record<string, string>> = {
  avenue: 'ave',
  boulevard: 'blvd',
  circle: 'cir',
  center: 'ctr',
  centre: 'ctr',
  court: 'ct',
  cove: 'cv',
  creek: 'crk',
  crossing: 'xing',
  drive: 'dr',
  east: 'e',
  expressway: 'expy',
  heights: 'hts',
  highway: 'hwy',
  lane: 'ln',
  north: 'n',
  northeast: 'ne',
  northwest: 'nw',
  parkway: 'pkwy',
  place: 'pl',
  point: 'pt',
  ridge: 'rdg',
  road: 'rd',
  south: 's',
  southeast: 'se',
  southwest: 'sw',
  square: 'sq',
  street: 'st',
  terrace: 'ter',
  trail: 'trl',
  west: 'w',
};

const normalizedStreet = (street: string): string =>
  street
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => STREET_TOKEN_ALIASES[token] ?? token)
    .join(' ');

export const streetsMateriallyDiffer = (inputStreet: string, standardizedStreet: string): boolean =>
  normalizedStreet(inputStreet) !== normalizedStreet(standardizedStreet);

export interface AddressValidationResult {
  address: SelectedAddress;
  results: ZeusAddressValidationResult[];
  needsManualEntry: boolean;
  needsUnitSelection: boolean;
}

/**
 * Resolves a selected address to its canonical casa record and reports what the
 * caller still has to collect: a full manual re-entry, or just a unit number.
 *
 * Google-sourced addresses are standardized first; casa-sourced ones already
 * carry their results and skip straight to the unit check.
 */
export const validateAddress = async (
  address: SelectedAddress,
  lookup: SellerAddressLookup = {
    standardizeAddress: standardizeSellerAddress,
    requiresUnitSelection: sellerAddressRequiresUnitSelection,
  },
): Promise<AddressValidationResult> => {
  let moreResult: ZeusAddressValidationResult[] = [];
  let casaAddress = address;

  if (address.source === 'google') {
    const addressResult = await lookup.standardizeAddress(address);
    if (!addressResult) {
      return {
        address,
        results: ['ADDRESS_UNKNOWN'],
        needsManualEntry: true,
        needsUnitSelection: false,
      };
    }

    if (
      addressResult.validationResults.some((result) => ADDRESS_INVALID_RESULT.includes(result)) &&
      streetsMateriallyDiffer(address.street1, addressResult.street)
    ) {
      return {
        address,
        results: ['ADDRESS_UNKNOWN'],
        needsManualEntry: true,
        needsUnitSelection: false,
      };
    }

    casaAddress = {
      id: addressResult.id,
      street1: addressResult.street,
      city: addressResult.city,
      state: addressResult.state,
      postal_code: addressResult.postalCode,
      // Casa's unit only. The seller's unit is applied below, and only when
      // the seller will confirm it again. Putting it here would skip that check
      // and submit the building id with a unit Casa never verified.
      unit: addressResult.unitNumber?.trim() || '',
      result: addressResult.validationResults,
      latitude: address.latitude,
      longitude: address.longitude,
      source: 'casa',
    };
  }

  if (!casaAddress?.result?.includes('ADDRESS_MISSING_SUB_PREMISE') && !casaAddress.unit?.trim()) {
    if (casaAddress.id && (await lookup.requiresUnitSelection(casaAddress.id))) {
      moreResult = ['ADDRESS_MISSING_SUB_PREMISE'];
    }
  }

  const combinedResults = [...(casaAddress?.result || []), ...moreResult];
  const needsManualEntry = ADDRESS_INVALID_RESULT.some((error) => combinedResults.includes(error));
  const needsUnitSelection = combinedResults.includes('ADDRESS_MISSING_SUB_PREMISE');
  const typedUnit = address.unit?.trim() ?? '';
  if (!casaAddress.unit?.trim() && typedUnit && (needsManualEntry || needsUnitSelection)) {
    casaAddress = { ...casaAddress, unit: typedUnit };
  }

  return {
    address: casaAddress,
    results: combinedResults,
    needsManualEntry,
    needsUnitSelection,
  };
};
